import { useEffect, useMemo, useState } from "react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { Navigate } from "react-router-dom";

import { db } from "@/firebase";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Loader from "@/components/Loader";

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  // 🔎 filters
  const [search, setSearch] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));

  // -------------------------
  // 🔐 ROLE PROTECTION
  // -------------------------
  if (!user) return <Navigate to="/login" />;
  if (!["admin", "super_admin"].includes(user.role))
    return <Navigate to="/dashboard" />;

  // -------------------------
  // 🔥 REALTIME LOGS
  // -------------------------
  useEffect(() => {
    const q = query(collection(db, "logs"), orderBy("created_at", "desc"));

    const unsub = onSnapshot(q, (snap) => {
      setLogs(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
      setLoadingLogs(false);
    });

    return () => unsub();
  }, []);

  // -------------------------
  // 🔍 FILTERED LOGS
  // -------------------------
  const filteredLogs = useMemo(() => {
    let list = [...logs];

    // 🔘 Today filter
    if (todayOnly) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      list = list.filter((log) => {
        if (!log.created_at?.toDate) return false;
        const logDate = log.created_at.toDate();
        logDate.setHours(0, 0, 0, 0);
        return logDate.getTime() === today.getTime();
      });
    }

    // 🔍 Ticket number search
    if (search.trim()) {
      list = list.filter(
        (log) =>
          log.ticket_number &&
          log.ticket_number.toString().includes(search.trim())
      );
    }

    return list;
  }, [logs, todayOnly, search]);

  // -------------------------
  // 🎨 BADGE COLOR
  // -------------------------
  const getActionColor = (action) => {
    switch (action) {
      case "created":
        return "bg-green-100 text-green-800 border-green-200";
      case "updated":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "comment":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "status":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "assign":
        return "bg-indigo-100 text-indigo-800 border-indigo-200";
      case "login":
        return "bg-cyan-100 text-cyan-800 border-cyan-200";
      case "logout":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // -------------------------
  // 🖥️ UI
  // -------------------------
  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* HEADER */}
        <div>
          <h1 className="text-3xl font-bold">Activity Logs</h1>
          <p className="text-gray-500 text-sm">
            Track all ticket, system, and login activities
          </p>
        </div>

        {/* 🔎 FILTER BAR */}
        <div className="flex flex-wrap gap-3 items-center">
          <Input
            placeholder="Search by Ticket #"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />

          <Button
            variant={todayOnly ? "default" : "outline"}
            onClick={() => setTodayOnly((p) => !p)}
          >
            {todayOnly ? "Showing Today" : "Today"}
          </Button>

          {(todayOnly || search) && (
            <Button
              variant="ghost"
              onClick={() => {
                setTodayOnly(false);
                setSearch("");
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* CONTENT */}
        {loadingLogs ? (
          <Loader />
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            No activity logs found
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLogs.map((log) => (
              <div
                key={log.id}
                className="border rounded-lg p-4 bg-white hover:shadow-sm transition"
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="space-y-1">
                    <p className="font-medium">
                      {log.ticket_number
                        ? `🎫 Ticket #${log.ticket_number}`
                        : "🔐 System Activity"}
                    </p>

                    {/* <p className="text-sm text-gray-700">{log.message}</p> */}
                    <p className="text-sm text-gray-700">
                      {log.message}
                    </p>

                    {log.action === "deleted" && log.metadata && (
                      <div className="mt-2 text-xs text-gray-600 bg-gray-50 border rounded p-3 space-y-1">
                        <p><b>Title:</b> {log.metadata.title}</p>
                        <p><b>Priority:</b> {log.metadata.priority}</p>
                        <p><b>Status before delete:</b> {log.metadata.status_before_delete}</p>
                        <p><b>Assigned to:</b> {log.metadata.assigned_developer}</p>

                        {log.metadata.reason && (
                          <p><b>Reason:</b> {log.metadata.reason}</p>
                        )}
                      </div>
                    )}


                    <p className="text-xs text-gray-500">
                      By {log.user_name || "System"}{" "}
                      {log.user_role && `(${log.user_role})`}
                    </p>

                    <p className="text-xs text-gray-400">
                      {log.created_at?.toDate?.().toLocaleString()}
                    </p>
                  </div>

                  <Badge
                    variant="outline"
                    className={getActionColor(log.action)}
                  >
                    {log.action}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
